/**
 * Earnings: the rider wallet ledger.
 *
 * Reads the settled total from the wallet projection rather than summing
 * client-side display strings, and renders each line through the shared `Money`
 * so the figure is formatted in one place.
 */
import { MinimalCard, MinimalEmptyState, MinimalInfoPanel } from '@ovasabi/ui-minimal'
import { WalletIcon } from '../../components/icons'
import { Money, PageHeader, Screen } from '../../components/chow'
import { useWalletTransactionSnapshot } from '../../generated/prototypeRuntime'
import { number, text, type Snapshot } from './records'
import { riderStyle as Style } from './riderStyle'

const SETTLED = ['settled', 'success', 'paid']

/** How far back the ledger goes on one screen. */
const LEDGER_LIMIT = 30

export const EarningsPage = () => {
  const wallet = useWalletTransactionSnapshot() as Snapshot
  const records = wallet.records
  const settled = records.filter((record) => SETTLED.includes(text(record, ['status']).toLowerCase()))
  const totalMinor = settled.reduce((sum, record) => sum + number(record, ['amount_minor']), 0)

  return (
    <Screen header={<PageHeader title="Earnings" accentDot />}>
      <MinimalInfoPanel
        tone="success"
        icon={<WalletIcon />}
        eyebrow="Available from recorded trips"
        title={<Money amount={totalMinor / 100} size="lg" />}
        description={`${settled.length} settled ${settled.length === 1 ? 'entry' : 'entries'}`}
      />
      {records.length === 0 ? (
        <MinimalEmptyState
          icon={<WalletIcon />}
          title="Your first earning will appear here"
          description="Complete a delivery and the settled amount will be added to your rider wallet history."
        />
      ) : (
        <Style.List>
          {records.slice(0, LEDGER_LIMIT).map((record, index) => (
            <MinimalCard key={text(record, ['id'], `earning-${index}`)} padding="sm" enter={false}>
              <Style.Top>
                <Style.Stack>
                  <Style.Title>{text(record, ['reference', 'transaction_type'], 'Delivery earning')}</Style.Title>
                  <Style.Meta>
                    {text(record, ['status'], 'Pending')} · {text(record, ['currency'], 'NGN')}
                  </Style.Meta>
                </Style.Stack>
                <Money amount={number(record, ['amount_minor']) / 100} />
              </Style.Top>
            </MinimalCard>
          ))}
        </Style.List>
      )}
    </Screen>
  )
}
